import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import isNil from "lodash/isNil";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";

import {
  CurrentBookingWizardStepAtom,
  isCreatingBookingAtom,
} from "../../atoms/stores";
import { useInitializeBookingWizard } from "../../hooks/useInitializeBookingWizard/useInitializeBookingWizard";
import { BookingWizard as BookingWizardType } from "../../types";
import { TimeSlotWizardStepContainer } from "../WizardStepContainer/TimeSlotWizardStepContainer";
import { BookingWizardUI } from "./BookingWizardUI";
import { BookingWizardQueryRendererQuery } from "./__generated__/BookingWizardQueryRendererQuery.graphql";

type BookingWizardQueryRendererProps = {
  queryRef: PreloadedQuery<BookingWizardQueryRendererQuery>;
  onDismiss: () => void;
  onCancel?: () => void;
  initializationOverides?: BookingWizardType.BookingWizardInitializationOverides;
  memberId: string;
  initiallyLockedSlotIds?: readonly string[];
};

/**
 * Standard booking wizard (Golf and Duration bookings)
 */
export const BookingWizardQueryRenderer = ({
  queryRef,
  onDismiss,
  onCancel,
  initializationOverides,
  memberId,
  initiallyLockedSlotIds,
}: BookingWizardQueryRendererProps) => {
  const data = usePreloadedQuery(
    BookingWizardQueryRendererQueryString,
    queryRef
  );

  const currentBookingWizardStep = useAtomValue(CurrentBookingWizardStepAtom);
  const isCreatingBooking = useAtomValue(isCreatingBookingAtom);

  const { isMissingRate } = useInitializeBookingWizard({
    sessionRef: data.session ?? null,
    nodeRef: data.node ?? null,
    initializationOverides,
    onClose: onDismiss,
  });

  if (
    !isMissingRate &&
    (isCreatingBooking || isNil(currentBookingWizardStep))
  ) {
    return <Loader />;
  }

  return (
    <BookingWizardUI
      isBookingUnavailable={isMissingRate}
      currentServerTime={data.currentServerTime}
      uiHeaderRef={data.club ?? null}
      uiFooterRef={data.node ?? null}
      onDismiss={onDismiss}
      onCancel={onCancel}
      StepContainer={
        isMissingRate ? (
          <VStack padding={4}>
            <Text>{t`No greens fee rate is available for your account at this time. Please contact the club to book.`}</Text>
          </VStack>
        ) : data?.node ? (
          <TimeSlotWizardStepContainer
            memberId={memberId}
            initiallyLockedSlotIds={initiallyLockedSlotIds}
            nodeRef={data.node}
            addPlayersRef={data.session?.currentClientProfile ?? null}
            addPlayersPrivateRef={data.session?.currentClientProfile ?? null}
            previousBookingsRef={data.session?.currentClientProfile ?? null}
            onClose={onDismiss}
          />
        ) : null
      }
    />
  );
};

/**
 * Query for standard bookings (Golf and Duration)
 */
export const BookingWizardQueryRendererQueryString = graphql`
  query BookingWizardQueryRendererQuery(
    $slotId: ID!
    $membershipTypeId: ID!
    $players: [ID]
    $currentDate: Date!
    $clubSlug: String!
    $slotType: SlotType
    $facilityId: ID!
    $bookingType: BookingType!
    $memberId: ID!
    $lessonTypeId: ID
    $bookingId: ID
  ) {
    currentServerTime
    club(slug: $clubSlug) {
      ...BookingWizardUIHeaderFragment
    }
    node(id: $slotId) {
      id
      ...AddPlayersStepNode
      ...useInitializeBookingWizardNode
      ...TimeSlotWizardStepContainerNode
        @arguments(memberId: $memberId, membershipTypeId: $membershipTypeId)
      ...BookingWizardUIFooterFragment
    }
    session {
      ...useInitializeBookingWizardSession @arguments(forSlotId: $slotId)
      currentClientProfile {
        ...AddPlayersStepClientProfile @arguments(players: $players)
        ...AddPlayersPrivateStepClientProfile
        ...PreviousBookingsStepClientProfile @arguments(forSlotId: $slotId)
      }
    }
  }
`;
